import { useTranslation } from 'react-i18next';
import type { ITaskStockClaim } from '@interfaces/IScheduler';
import { ChevronRightIcon } from '@utils/icons';
import type { ICartStatus } from '../../stock/cartItem';
import styles from './StockCart.module.css';

interface IStockCartItemProps {
  claim: ITaskStockClaim;
  /** What it books — element types and floors (`bookedLabel`). */
  what: string;
  /** Where it stands (`cartStatus`). */
  status: ICartStatus;
  /** Reopens the row's form. */
  onEdit: () => void;
  /** Drops the material from the list. */
  onRemove: () => void;
  /** Deep-link to a delivery on the Logistics page; absent where the drawer's
   *  host has no router, and then the order's name is inert. */
  onOpenDelivery?: (deliveryRef: string) => void;
}

/**
 * A material on the Stock tab's list while nobody is editing it (contract
 * v14): its name and amount, what it books, one status line with a dot, and
 * Edit / Remove. Ordering happens once, at the checkout — never from here.
 */
export function StockCartItem({ claim, what, status, onEdit, onRemove, onOpenDelivery }: IStockCartItemProps) {
  const { t } = useTranslation();
  const { jumpRef } = status;
  return (
    <div className={styles.item}>
      <div className={styles.itemTop}>
        <span className={styles.itemName}>{claim.materialName}</span>
        <span className={styles.itemQty}>{`${claim.quantity} ${claim.unit}`}</span>
      </div>
      <span className={styles.itemWhat}>{what}</span>
      <div className={styles.itemStatus}>
        <span className={styles.dot} data-tone={status.tone} aria-hidden />
        {jumpRef === null ? (
          <span>{status.text}</span>
        ) : (
          <button type="button" className={styles.jump} onClick={() => onOpenDelivery?.(jumpRef)}>
            {status.text}
            <ChevronRightIcon size={12} />
          </button>
        )}
        <span className={styles.itemActs}>
          <button type="button" className={styles.act} onClick={onEdit}>
            {t('scheduler.panel.stock.cart.edit')}
          </button>
          <button type="button" className={styles.act} onClick={onRemove}>
            {t('scheduler.panel.stock.cart.remove')}
          </button>
        </span>
      </div>
    </div>
  );
}

interface IStockCheckoutBarProps {
  count: number;
  onReview: () => void;
}

/** Under the list: how many materials still need ordering, and the one way
 *  to order them — the review. */
export function StockCheckoutBar({ count, onReview }: IStockCheckoutBarProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.checkout}>
      <b>{t('scheduler.panel.stock.cart.toOrder', { count })}</b>
      <button type="button" className={styles.primary} onClick={onReview}>
        {t('scheduler.panel.stock.cart.review')}
      </button>
    </div>
  );
}
